import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import './Avis.css';
import { 
  FaStar, 
  FaStarHalfAlt, 
  FaRegStar,
  FaComments,
  FaThumbsUp,
  FaThumbsDown,
  FaSmile,
  FaFrown,
  FaMeh,
  FaSpinner,
  FaSearch,
  FaCalendarAlt,
  FaChartBar,
  FaFileExcel,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';

const Avis = () => {
  const [avis, setAvis] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showStats, setShowStats] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' ou 'list'
  const [exporting, setExporting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchAvis = async () => {
      setLoading(true);
      try {
      const querySnapshot = await getDocs(collection(db, 'satisfaction'));
      const avisList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAvis(avisList);

      // Collect all feedbacks
      const feedbackList = avisList
        .filter(review => review.feedback !== "")
        .map(review => ({
          text: review.feedback,
            date: review.date?.toDate() || new Date(),
            application: review.application || 0,
            service: review.service || 0,
            plats: review.plats || 0,
            globalRating: ((review.application || 0) + (review.service || 0) + (review.plats || 0)) / 3
        }));
      setFeedbacks(feedbackList);
      } catch (error) {
        console.error('Erreur lors du chargement des avis:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAvis();
  }, []);

  // Function to calculate the average of an array of numbers
  const calculateAverage = (values) => {
    if (values.length === 0) return 0;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return (sum / values.length).toFixed(1);
  };

  // Extract ratings for application, service, and plats
  const applicationRatings = avis.map(review => review.application || 0);
  const serviceRatings = avis.map(review => review.service || 0);
  const platsRatings = avis.map(review => review.plats || 0);

  // Calculate averages
  const averageApplication = calculateAverage(applicationRatings);
  const averageService = calculateAverage(serviceRatings);
  const averagePlats = calculateAverage(platsRatings);

  // Global average (average of all three categories)
  const globalAverage = calculateAverage([
    ...applicationRatings,
    ...serviceRatings,
    ...platsRatings
  ]);

  // Fonction pour générer les étoiles
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className="avis-star avis-star-filled" />);
    }
    
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" className="avis-star avis-star-half" />);
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaRegStar key={`empty-${i}`} className="avis-star avis-star-empty" />);
    }
    
    return stars;
  };

  // Fonction pour obtenir l'icône de sentiment
  const getSentimentIcon = (rating) => {
    if (rating >= 4) return <FaSmile className="avis-sentiment-icon avis-sentiment-positive" />;
    if (rating >= 3) return <FaMeh className="avis-sentiment-icon avis-sentiment-neutral" />;
    return <FaFrown className="avis-sentiment-icon avis-sentiment-negative" />;
  };

  // Fonction pour obtenir la couleur de la note
  const getRatingColor = (rating) => {
    if (rating >= 4) return '#10b981'; // Vert
    if (rating >= 3) return '#f59e0b'; // Orange
    return '#ef4444'; // Rouge
  };

  // Filtrage et tri des feedbacks
  const filteredFeedbacks = feedbacks.filter(feedback => {
    const matchesSearch = feedback.text.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRating = filterRating === 'all' || 
      (filterRating === 'high' && feedback.globalRating >= 4) ||
      (filterRating === 'medium' && feedback.globalRating >= 3 && feedback.globalRating < 4) ||
      (filterRating === 'low' && feedback.globalRating < 3);
    return matchesSearch && matchesRating;
  }).sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'date') {
      comparison = b.date - a.date;
    } else if (sortBy === 'rating') {
      comparison = b.globalRating - a.globalRating;
    } else if (sortBy === 'text') {
      comparison = a.text.localeCompare(b.text);
    }
    return sortOrder === 'asc' ? -comparison : comparison;
  });

  // Statistiques avancées
  const stats = {
    totalReviews: avis.length,
    totalFeedbacks: feedbacks.length,
    averageApplication: parseFloat(averageApplication),
    averageService: parseFloat(averageService),
    averagePlats: parseFloat(averagePlats),
    globalAverage: parseFloat(globalAverage),
    positiveReviews: feedbacks.filter(f => f.globalRating >= 4).length,
    neutralReviews: feedbacks.filter(f => f.globalRating >= 3 && f.globalRating < 4).length,
    negativeReviews: feedbacks.filter(f => f.globalRating < 3).length
  };

  // Export Excel
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      
      const excelData = feedbacks.map(feedback => ({
        'Date': feedback.date.toLocaleDateString('fr-FR'),
        'Heure': feedback.date.toLocaleTimeString('fr-FR'),
        'Application': feedback.application,
        'Service': feedback.service,
        'Plats': feedback.plats,
        'Note Globale': feedback.globalRating.toFixed(1),
        'Feedback': feedback.text
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Avis Clients");
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avis-clients-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="avis-container">
      {/* Header */}
      <div className="avis-header">
        <div className="avis-title-section">
          <FaComments className="avis-title-icon" />
          <h1 className="avis-title">Avis Clients</h1>
        </div>
        <p className="avis-subtitle">
          Analyse et suivi de la satisfaction client
        </p>
      </div>

      {/* Statistiques */}
      {showStats && (
        <div className="avis-stats-section">
          <div className="avis-stats-grid">
            <div className="avis-stat-card">
              <div className="avis-stat-icon">
                <FaComments />
              </div>
              <div className="avis-stat-content">
                <div className="avis-stat-value">{stats.totalReviews}</div>
                <div className="avis-stat-label">Avis Totaux</div>
              </div>
            </div>
            <div className="avis-stat-card">
              <div className="avis-stat-icon">
                <FaThumbsUp />
              </div>
              <div className="avis-stat-content">
                <div className="avis-stat-value">{stats.positiveReviews}</div>
                <div className="avis-stat-label">Positifs</div>
              </div>
            </div>
            <div className="avis-stat-card">
              <div className="avis-stat-icon">
                <FaMeh />
              </div>
              <div className="avis-stat-content">
                <div className="avis-stat-value">{stats.neutralReviews}</div>
                <div className="avis-stat-label">Neutres</div>
              </div>
            </div>
            <div className="avis-stat-card">
              <div className="avis-stat-icon">
                <FaThumbsDown />
              </div>
              <div className="avis-stat-content">
                <div className="avis-stat-value">{stats.negativeReviews}</div>
                <div className="avis-stat-label">Négatifs</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes moyennes */}
      {showStats && (
        <div className="avis-ratings-section">
          <h3 className="avis-section-title">
            <FaChartBar className="avis-section-icon" />
            Notes Moyennes
          </h3>
          <div className="avis-ratings-grid">
            <div className="avis-rating-card">
              <div className="avis-rating-header">
                <h4>Application</h4>
                <div className="avis-rating-stars">
                  {renderStars(stats.averageApplication)}
                </div>
              </div>
              <div className="avis-rating-value" style={{ color: getRatingColor(stats.averageApplication) }}>
                {stats.averageApplication}/5
              </div>
              <div className="avis-progress-bar">
                <div 
                  className="avis-progress-fill"
                  style={{ 
                    width: `${(stats.averageApplication / 5) * 100}%`,
                    backgroundColor: getRatingColor(stats.averageApplication)
                  }}
        ></div>
      </div>
            </div>
            
            <div className="avis-rating-card">
              <div className="avis-rating-header">
                <h4>Service</h4>
                <div className="avis-rating-stars">
                  {renderStars(stats.averageService)}
                </div>
              </div>
              <div className="avis-rating-value" style={{ color: getRatingColor(stats.averageService) }}>
                {stats.averageService}/5
              </div>
              <div className="avis-progress-bar">
                <div 
                  className="avis-progress-fill"
                  style={{ 
                    width: `${(stats.averageService / 5) * 100}%`,
                    backgroundColor: getRatingColor(stats.averageService)
                  }}
        ></div>
      </div>
            </div>
            
            <div className="avis-rating-card">
              <div className="avis-rating-header">
                <h4>Plats</h4>
                <div className="avis-rating-stars">
                  {renderStars(stats.averagePlats)}
                </div>
              </div>
              <div className="avis-rating-value" style={{ color: getRatingColor(stats.averagePlats) }}>
                {stats.averagePlats}/5
              </div>
              <div className="avis-progress-bar">
                <div 
                  className="avis-progress-fill"
                  style={{ 
                    width: `${(stats.averagePlats / 5) * 100}%`,
                    backgroundColor: getRatingColor(stats.averagePlats)
                  }}
        ></div>
      </div>
            </div>
            
            <div className="avis-rating-card avis-rating-card-global">
              <div className="avis-rating-header">
                <h4>Note Globale</h4>
                <div className="avis-rating-stars">
                  {renderStars(stats.globalAverage)}
                </div>
              </div>
              <div className="avis-rating-value avis-rating-value-global" style={{ color: getRatingColor(stats.globalAverage) }}>
                {stats.globalAverage}/5
              </div>
              <div className="avis-progress-bar">
                <div 
                  className="avis-progress-fill"
                  style={{ 
                    width: `${(stats.globalAverage / 5) * 100}%`,
                    backgroundColor: getRatingColor(stats.globalAverage)
                  }}
        ></div>
      </div>
            </div>
          </div>
        </div>
      )}

      {/* Contrôles */}
      <div className="avis-controls-section">
        <div className="avis-filters">
          <div className="avis-search-container">
            <FaSearch className="avis-search-icon" />
            <input
              type="text"
              placeholder="Rechercher dans les feedbacks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="avis-search-input"
            />
          </div>
          
          <select
            value={filterRating}
            onChange={(e) => setFilterRating(e.target.value)}
            className="avis-filter-select"
          >
            <option value="all">Toutes les notes</option>
            <option value="high">Notes élevées (4-5)</option>
            <option value="medium">Notes moyennes (3-4)</option>
            <option value="low">Notes faibles (1-3)</option>
          </select>
        </div>

        <div className="avis-actions">
          <button
            onClick={() => setShowStats(!showStats)}
            className="avis-toggle-stats-btn"
          >
            {showStats ? <FaArrowUp /> : <FaArrowDown />}
            {showStats ? 'Masquer' : 'Afficher'} Stats
          </button>
          
          <button
            onClick={() => setViewMode(viewMode === 'cards' ? 'list' : 'cards')}
            className="avis-view-mode-btn"
          >
            {viewMode === 'cards' ? <FaEye /> : <FaEyeSlash />}
            {viewMode === 'cards' ? 'Vue Liste' : 'Vue Cartes'}
          </button>
          
          <button
            onClick={exportToExcel}
            disabled={exporting}
            className="avis-export-btn"
          >
            {exporting ? <FaSpinner className="spinning" /> : <FaFileExcel />}
            {exporting ? 'Export...' : 'Exporter'}
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="avis-loading-container">
          <div className="avis-loading-spinner">
            <FaSpinner className="spinning" />
          </div>
          <p className="avis-loading-text">Chargement des avis...</p>
        </div>
      )}

      {/* Feedbacks */}
      {!loading && (
        <div className="avis-feedbacks-section">
          <div className="avis-feedbacks-header">
            <h3 className="avis-section-title">
              <FaComments className="avis-section-icon" />
              Feedbacks Clients ({filteredFeedbacks.length})
            </h3>
            <div className="avis-sort-controls">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="avis-sort-select"
              >
                <option value="date">Trier par date</option>
                <option value="rating">Trier par note</option>
                <option value="text">Trier par texte</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="avis-sort-order-btn"
              >
                {sortOrder === 'asc' ? <FaArrowUp /> : <FaArrowDown />}
              </button>
            </div>
          </div>

          {filteredFeedbacks.length > 0 ? (
            <div className={`avis-feedbacks-container ${viewMode === 'list' ? 'avis-feedbacks-list' : 'avis-feedbacks-cards'}`}>
              {filteredFeedbacks.map((feedback, index) => (
                <div key={index} className="avis-feedback-card">
                  <div className="avis-feedback-header">
                    <div className="avis-feedback-rating">
                      <div className="avis-feedback-stars">
                        {renderStars(feedback.globalRating)}
                      </div>
                      <span className="avis-feedback-rating-value" style={{ color: getRatingColor(feedback.globalRating) }}>
                        {feedback.globalRating.toFixed(1)}/5
                      </span>
                    </div>
                    <div className="avis-feedback-sentiment">
                      {getSentimentIcon(feedback.globalRating)}
                    </div>
                    <div className="avis-feedback-date">
                      <FaCalendarAlt className="avis-date-icon" />
                      <span>
                  {isMobile 
                    ? feedback.date.toLocaleDateString('fr-FR')
                    : feedback.date.toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                  }
                </span>
                    </div>
                  </div>
                  
                  <div className="avis-feedback-content">
                    <p className="avis-feedback-text">{feedback.text}</p>
                  </div>
                  
                  <div className="avis-feedback-details">
                    <div className="avis-feedback-breakdown">
                      <div className="avis-breakdown-item">
                        <span className="avis-breakdown-label">App:</span>
                        <div className="avis-breakdown-stars">
                          {renderStars(feedback.application)}
                        </div>
                      </div>
                      <div className="avis-breakdown-item">
                        <span className="avis-breakdown-label">Service:</span>
                        <div className="avis-breakdown-stars">
                          {renderStars(feedback.service)}
                        </div>
                      </div>
                      <div className="avis-breakdown-item">
                        <span className="avis-breakdown-label">Plats:</span>
                        <div className="avis-breakdown-stars">
                          {renderStars(feedback.plats)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="avis-no-feedbacks">
              <FaExclamationTriangle className="avis-no-feedbacks-icon" />
              <p>Aucun feedback trouvé pour cette recherche.</p>
            </div>
        )}
      </div>
      )}
    </div>
  );
};

export default Avis;
